import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { CodeNode } from '@lexical/code';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { TRANSFORMERS } from '@lexical/markdown';
import ToolbarPlugin from './LexicalToolbarPlugin';
import { EditorState } from 'lexical';
import {
  $convertToMarkdownString,
  $convertFromMarkdownString
} from '@lexical/markdown';

interface LexicalPromptEditorProps {
  initialValue: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
  isDarkMode?: boolean;
  height?: number;
}

// Plugin para manejar la conversión a Markdown
function MarkdownConverterPlugin({
  onChange
}: {
  onChange: (markdown: string) => void;
}) {
  const handleChange = (editorState: EditorState) => {
    editorState.read(() => {
      const markdown = $convertToMarkdownString(TRANSFORMERS);
      onChange(markdown);
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

const LexicalPromptEditor: React.FC<LexicalPromptEditorProps> = ({
  initialValue,
  onChange,
  placeholder = 'Start writing your prompt instructions...\n\nUse:\n- # for main titles\n- ## for sections\n- ### for subsections\n- **text** for emphasis',
  isDarkMode = false,
  height = 500
}) => {
  // Configuración del tema
  const theme = {
    ltr: 'ltr',
    rtl: 'rtl',
    paragraph: 'editor-paragraph',
    quote: 'editor-quote',
    heading: {
      h1: 'editor-heading-h1',
      h2: 'editor-heading-h2',
      h3: 'editor-heading-h3',
      h4: 'editor-heading-h4',
      h5: 'editor-heading-h5'
    },
    list: {
      nested: {
        listitem: 'editor-nested-listitem'
      },
      ol: 'editor-list-ol',
      ul: 'editor-list-ul',
      listitem: 'editor-listitem'
    },
    text: {
      bold: 'editor-text-bold',
      italic: 'editor-text-italic',
      underline: 'editor-text-underline',
      strikethrough: 'editor-text-strikethrough',
      code: 'editor-text-code'
    },
    code: 'editor-code'
  };

  // Configuración inicial del editor
  const initialConfig = {
    namespace: 'PromptEditor',
    theme,
    onError: (error: Error) => {
      console.error('Lexical error:', error);
    },
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNode,
      ListItemNode,
      CodeNode,
      LinkNode,
      AutoLinkNode
    ],
    editorState: () => {
      // Inicializar con el contenido markdown
      $convertFromMarkdownString(initialValue || '', TRANSFORMERS);
    }
  };

  return (
    <div
      className={`lexical-prompt-editor ${
        isDarkMode ? 'dark-theme' : 'light-theme'
      }`}
    >
      <LexicalComposer initialConfig={initialConfig}>
        <div className="editor-container border rounded bg-body scrollbar mb-3">
          <ToolbarPlugin />
          <div
            className="editor-scroller scrollbar"
            style={{ height: `${height}px` }}
          >
            <div className="editor">
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    className="editor-input"
                    aria-placeholder={placeholder}
                    placeholder={
                      <div className="editor-placeholder">{placeholder}</div>
                    }
                  />
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
              <MarkdownConverterPlugin onChange={onChange} />
            </div>
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
};

export default LexicalPromptEditor;
